import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import dig from 'object-dig';

import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import MonetizationOnIcon from '@material-ui/icons/MonetizationOn';
import CloseIcon from '@material-ui/icons/Close';
import RemoveIcon from '@material-ui/icons/Remove';
import CheckIcon from '@material-ui/icons/Check';

import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';

import LinearProgress from '~/components/LinearProgress';
import GridContainer from '~/components/GridContainer';
import IconButton from '~/components/Button/IconButton';
import GridItem from '~/components/GridItem';
import Button from '~/components/Button';
import ModalDelete from '~/components/ModalDelete';
import Paper from '~/components/Paper';
import Table from '~/components/Table/Table';
import tableStyles from '~/components/Table/Table/styles';

import Request from '~/providers/Request';
import isPresent from '~/utils/isPresent';
import formatNumber from '~/utils/Number/formatNumber';

import New from './New';
import Edit from './Edit';
import ModalPayments from './ModalPayments';

function Financial({ id }) {
  const { t } = useTranslation();
  const classesTable = tableStyles();

  const [loading, setLoading] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);

  const [isNew, setIsNew] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [idPaymenetEdit, setIdPaymenetEdit] = useState(null);
  const [isModalPyasments, setIsModalPyasments] = useState(false);

  const [payments, setPayments] = useState([]);
  const [reload, setReload] = useState(false);

  const [paymentRemove, setPaymentRemove] = useState([]);

  async function handleDelete() {
    setLoadingDelete(true);

    Request.del(
      `/admin/corporation/payment_services/${paymentRemove.id}`,
      null,
      { normalizeJSON: false }
    )
      .then(() => {
        setPaymentRemove(null);
        setLoadingDelete(false);
        setReload(!reload);
        toast.success(t('delete-toast-success'));
      })
      .catch((e) => {
        console.error(e);
        setLoadingDelete(false);
      });
  }

  useEffect(() => {
    async function getPaymentServices() {
      setLoading(true);
      Request.get(`/admin/corporation/${id}/payment_services`, {})
        .then((response) => {
          setLoading(false);
          setPayments(response.data);
        })
        .catch((e) => {
          console.error(e);
          setLoading(false);
        });
    }
    getPaymentServices();
  }, [id, reload]);

  return (
    <>
      <LinearProgress loading={loading} />

      <Paper
        Icon={MonetizationOnIcon}
        iconColor="purple"
        title={t('corporation.tabs.financial')}
        loading={loading}
      >
        <GridContainer>
          <GridItem
            xs={12}
            sm={12}
            md={12}
            lg={12}
            xl={12}
            style={{ display: 'block', textAlign: 'right' }}
          >
            <Button color="success" onClick={() => setIsNew(true)}>
              {t('button-menu-new')}
            </Button>
            {payments.length > 0 && (
              <Button
                style={{ margin: 5 }}
                color="purple"
                onClick={() => setIsModalPyasments(true)}
              >
                {t('corporation.extras.showPayments')}
              </Button>
            )}
          </GridItem>
        </GridContainer>

        <GridContainer>
          <GridItem xs={12} sm={12} md={12} lg={12} xl={12}>
            <Table
              emptyData={payments.length === 0}
              loading={loading}
              columns={
                <>
                  <TableCell className={classesTable.cell}>
                    {t('action.actions')}
                  </TableCell>
                  <TableCell className={classesTable.cell}>
                    {t('corporationPaymentServices.attributes.service')}
                  </TableCell>
                  <TableCell className={classesTable.cell}>
                    {t('corporationPaymentServices.attributes.price')}
                  </TableCell>
                  <TableCell className={classesTable.cell}>
                    {t('corporationPaymentServices.attributes.is_fixed')}
                  </TableCell>
                  <TableCell className={classesTable.cell}>
                    {t('corporationPaymentServices.attributes.fixed_amount')}
                  </TableCell>
                  <TableCell className={classesTable.cell}>
                    {t('corporationPaymentServices.attributes.excess_price')}
                  </TableCell>
                  <TableCell className={classesTable.cell}>
                    {t('corporationPaymentServices.attributes.payment_wallet')}
                  </TableCell>
                  <TableCell className={classesTable.cell}>
                    {t('corporationPaymentServices.attributes.payment_method_id')}
                  </TableCell>
                </>
              }
            >
              {payments.map((row) => {
                return (
                  <TableRow key={row.id} hover className={classesTable.row}>
                    <TableCell>
                      <IconButton
                        tooltip={t('action.edit')}
                        onClick={() => {
                          setIdPaymenetEdit(row.id);
                          setIsEdit(true);
                        }}
                        Icon={EditIcon}
                        iconColor="orange"
                      />

                      <IconButton
                        tooltip={t('action.delete')}
                        onClick={() => {
                          setPaymentRemove(row);
                        }}
                        Icon={DeleteIcon}
                        iconColor="red"
                      />
                    </TableCell>
                    <TableCell>{dig(row, 'payment_service', 'name')}</TableCell>
                    <TableCell>{formatNumber(dig(row, 'price'))}</TableCell>
                    <TableCell>
                      {row.is_fixed ? <CheckIcon /> : <CloseIcon />}
                    </TableCell>
                    <TableCell>
                      {row.is_fixed ? row.fixed_amount : <RemoveIcon />}
                    </TableCell>
                    <TableCell>
                      {row.is_fixed ? (
                        formatNumber(row.excess_price)
                      ) : (
                          <RemoveIcon />
                        )}
                    </TableCell>
                    <TableCell>{dig(row, 'payment_wallet', 'name')}</TableCell>
                    <TableCell>{dig(row, 'payment_method', 'name')}</TableCell>
                  </TableRow>
                );
              })}
            </Table>
          </GridItem>
        </GridContainer>
      </Paper>

      <New
        isOpen={isNew}
        id={id}
        handleClose={() => setIsNew(false)}
        onSuccess={() => {
          setIsNew(false);
          setReload(!reload);
        }}
      />

      <Edit
        isOpen={isEdit}
        id={id}
        idPayment={idPaymenetEdit}
        handleClose={() => setIsEdit(false)}
        onSuccess={() => {
          setIsEdit(false);
          setReload(!reload);
        }}
      />

      <ModalDelete
        modal={isPresent(paymentRemove)}
        isLoading={loadingDelete}
        message={t('delete-modal-text')}
        buttonText={t('action.remove')}
        deleteAction={handleDelete}
        onClose={() => {
          setPaymentRemove(null);
        }}
      />

      <ModalPayments
        open={isModalPyasments}
        id={id}
        onClose={() => {
          setIsModalPyasments(false);
        }}
      />

      <LinearProgress loading={loading} />
    </>
  );
}

export default React.memo(Financial);
